import React, { useMemo } from "react";

import { Divider, Grid } from "@material-ui/core";
import Big from "big.js";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { SlimSecondaryButton } from "components/Button";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import { SelectField } from "components/Form/FormikControls";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { ModalTypes } from "constants/navigation";
import { SplitOrigin, SplitType, SplitTypeOptions } from "constants/splits";

import { openModalLink } from "lib/navigation";
import { getSplitKey } from "lib/splits";

import {
  getBuyerSplitByKey,
  getDeploymentIdBySaleLotId,
  getSaleLotById,
  selectBuyerSplitByKeyLookup,
  selectDescriptionByVendorSplitIdLookup,
} from "selectors";

import { useBoolean } from "hooks";
import { DefaultBuyerSplitAction, SaleBuyerSplitAction } from "actions";
import { SplitAllocationForm } from "components/TransactionSplits/SplitAllocationForm";

interface Props {
  primaryBusinessId: string;
  onClose: () => void;
  saleLotId: string;
  livestockSaleId?: number;
  isDefault?: boolean;
}

interface FormValues {
  primaryBusinessId: string;
  saleLotId: string;
  isDefault: boolean;
  type: string;
  livestockSaleId: number;
  splits: TransactionSplitAllocation[];
}

export const BuyerSplitModal = ({
  primaryBusinessId,
  livestockSaleId,
  saleLotId,
  isDefault,
  onClose,
}: Props) => {
  const dispatch = useDispatch();
  const deploymentId: number = useSelector(
    getDeploymentIdBySaleLotId(saleLotId),
  );

  const action = isDefault ? DefaultBuyerSplitAction : SaleBuyerSplitAction;
  const handleSubmit = (values: FormValues) => {
    const payload = {
      ...values,
      deploymentId,
    };

    // Dont include livestock sale if we have a saleLot.
    if (payload.saleLotId) {
      payload.livestockSaleId = null;
    }

    dispatch(action.create(payload));

    onClose();
  };

  const filterFields = {
    primaryBusinessId,
    deploymentId,
    saleLotId,
    livestockSaleId,
  };

  const existingSplit = useSelector(
    getBuyerSplitByKey(getSplitKey(filterFields)),
  );

  const onConfirmDelete = () => {
    dispatch(action.delete(existingSplit.id));
    onClose();
  };

  const initialValues = {
    primaryBusinessId,
    saleLotId,
    isDefault,
    type: SplitType.PERCENTAGE,
    livestockSaleId,
    splits: [{ businessId: primaryBusinessId, amount: 1 }],
    ...existingSplit,
  };

  const descriptionBySplitIdLookup = useSelector(
    selectDescriptionByVendorSplitIdLookup,
  );

  const saleLot = useSelector(getSaleLotById(saleLotId));

  const buyerSplitByKeyLookup = useSelector(selectBuyerSplitByKeyLookup);

  const saleSplitKey = getSplitKey({
    primaryBusinessId,
    deploymentId,
    livestockSaleId,
  });
  const saleSplit = buyerSplitByKeyLookup[saleSplitKey];
  const splitType = "Buyer";
  const splitMessage = useMemo(() => {
    const modalAction = existingSplit ? "Update" : "Create";

    if (saleLot) {
      return `${modalAction} ${splitType} Split for Sale Lot #${saleLot.lot_number} (${saleLot.quantity})`;
    }
    return `${modalAction} Sale ${splitType} Split`;
  }, [existingSplit, saleLot]);

  const validate = (values: FormValues) => {
    const errors: { splits: string[] } = { splits: [] };

    const totalAmount = values.splits.reduce(
      (total: Big, transactionSplit: TransactionSplitAllocation) =>
        total.plus(new Big(transactionSplit.amount || 0)),
      new Big(0),
    );

    if (values.splits.some(split => !split.businessId)) {
      errors.splits.push("All splits must have a business.");
    }

    if (values.splits.some(split => !split.amount)) {
      errors.splits.push("All splits must have an amount.");
    }

    if (values.type === SplitType.PERCENTAGE) {
      if (!totalAmount.eq(1)) {
        errors.splits.push(
          `Split percentages must add up to 100% (${new Big(1)
            .minus(totalAmount)
            .times(100)}% difference).`,
        );
      }
    }
    if (values.type === SplitType.HEADCOUNT && !saleLotId) {
      errors.splits.push("Headcount splits must be on a Sale Lot.");
    }
    if (
      values.type === SplitType.HEADCOUNT &&
      !totalAmount.eq(saleLot.quantity)
    ) {
      errors.splits.push(
        `Total split amount must equal Sale Lot headcount (${totalAmount
          .times(-1)
          .plus(saleLot.quantity)} difference)`,
      );
    }

    if (errors.splits.length === 0) {
      delete errors.splits;
    }
    return errors;
  };
  const [isShowingConfirmDelete, showConfirmDelete, hideConfirmDelete] =
    useBoolean(false);

  const onClickEditDefault = () =>
    openModalLink(ModalTypes.BuyerSplit, {
      deploymentId,
      primaryBusinessId,
    });

  const showSaleOverride = saleSplit && saleLotId;

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={onClose}>{splitMessage}</DialogTitle>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={validate}
      >
        <Form>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {showSaleOverride && (
                  <Grid item xs={12}>
                    This will override the existing
                    <SlimSecondaryButton onClick={onClickEditDefault}>
                      Split for the Sale
                    </SlimSecondaryButton>{" "}
                    on the buyer ({descriptionBySplitIdLookup[saleSplit.id]})
                  </Grid>
                )}

                {showSaleOverride && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
                {saleLotId && (
                  <Grid item xs={6}>
                    <SelectField
                      name="type"
                      label="Type"
                      tooltip="Only Sale Lots can be split by head count."
                      options={SplitTypeOptions}
                      menuPortalTarget={document.body}
                    />
                  </Grid>
                )}
              </Grid>
              <SplitAllocationForm splitType={SplitOrigin.BUYER} />
              {saleLotId && (
                <Grid item xs={12}>
                  Note: Splits on a Sale Lot will override those on a Sale.
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <SecondaryButton type="button" onClick={onClose}>
              Back
            </SecondaryButton>
            <DeleteButton
              type="button"
              onClick={showConfirmDelete}
              disabled={!existingSplit?.id}
            >
              Delete
            </DeleteButton>
            <Button data-tour="submit" type="submit">
              Save
            </Button>
          </DialogActions>
          <ConfirmDialog
            title={createModalTitle(`this ${splitType} Split`)}
            isOpen={isShowingConfirmDelete}
            onCancel={hideConfirmDelete}
            onDelete={onConfirmDelete}
          />
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};
